import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';
import { formatDateShort } from '../../utils/dateUtils';
import { 
  PlusCircleIcon, 
  TruckIcon, 
  CalendarIcon, 
  UserGroupIcon,
  ClockIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

const Dashboard = () => {
  const [stats, setStats] = useState({
    activeRides: 0,
    totalBookings: 0,
    vehicles: 0
  });
  const [upcomingRides, setUpcomingRides] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch rides with detailed logging
      console.log('Fetching driver rides...');
      const ridesRes = await axiosInstance.get(API_URLS.rides.driver);
      console.log('Rides response:', ridesRes.data);
      
      // Fetch bookings
      const bookingsRes = await axiosInstance.get(API_URLS.bookings.driver);
      
      // Fetch vehicles
      const vehiclesRes = await axiosInstance.get(API_URLS.vehicles);

      // Improved filtering for upcoming rides
      const today = new Date();
      const todayString = today.getFullYear() + '-' + 
                         String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                         String(today.getDate()).padStart(2, '0');
      
      const activeRides = ridesRes.data.filter(ride => {
        // Compare date strings directly to avoid timezone issues
        const rideDate = ride.date;
        
        // Log each ride for debugging
        console.log(`Ride ${ride.id}: date=${rideDate}, today=${todayString}, status=${ride.status}, comparison=${rideDate >= todayString}`);
        
        // Include rides that are today or in the future and have an available status
        return rideDate >= todayString && (ride.status === 'available' || ride.status === 'scheduled');
      });
      
      // Sort rides by date and time in ascending order
      const sortedRides = [...activeRides].sort((a, b) => {
        // First compare by date
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB;
        }
        
        // If dates are the same, compare by time
        // Convert time strings to comparable values
        const timeToMinutes = (timeStr) => {
          if (!timeStr) return 0;
          const [hours, minutes] = timeStr.split(':').map(Number);
          return hours * 60 + minutes;
        };
        
        const timeA = timeToMinutes(a.time);
        const timeB = timeToMinutes(b.time);
        
        return timeA - timeB;
      });
      
      console.log('Filtered and sorted active rides:', sortedRides);

      setStats({
        activeRides: activeRides.length,
        totalBookings: bookingsRes.data.length,
        vehicles: vehiclesRes.data.length
      });

      setUpcomingRides(sortedRides);
      setRecentBookings(bookingsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Driver Dashboard</h1>
      
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-indigo-100 rounded-md p-3">
              <CalendarIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Active Rides
                </dt>
                <dd>
                  <div className="text-lg font-medium text-gray-900">
                    {loading ? '...' : stats.activeRides}
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div className="bg-white overflow-hidden shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-indigo-100 rounded-md p-3">
              <CalendarIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Upcoming Rides
                </dt>
                <dd>
                  <div className="text-lg font-medium text-gray-900">
                    {loading ? '...' : upcomingRides.length}
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div className="bg-white overflow-hidden shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-indigo-100 rounded-md p-3">
              <UserGroupIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Recent Bookings
                </dt>
                <dd>
                  <div className="text-lg font-medium text-gray-900">
                    {loading ? '...' : recentBookings.length}
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div className="bg-white overflow-hidden shadow rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-indigo-100 rounded-md p-3">
              <UserGroupIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total Bookings
                </dt>
                <dd>
                  <div className="text-lg font-medium text-gray-900">
                    {loading ? '...' : stats.totalBookings}
                  </div>
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-8">
        {/* Upcoming Rides */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h2 className="text-lg leading-6 font-medium text-gray-900">Upcoming Rides</h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">Your next scheduled rides</p>
            </div>
            <Link 
              to="/driver/rides" 
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
            >
              View All
            </Link>
          </div>
          
          {loading ? (
            <div className="px-4 py-5 sm:p-6 text-center">
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : upcomingRides.length === 0 ? (
            <div className="px-4 py-5 sm:p-6 text-center">
              <p className="text-gray-500">No upcoming rides scheduled</p>
            </div>
          ) : (
            <div className="border-t border-gray-200 divide-y divide-gray-200">
              {upcomingRides.slice(0, 5).map((ride) => (
                <div key={ride.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-indigo-100 rounded-md p-2">
                        <CalendarIcon className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-sm font-medium text-gray-900">
                          {formatDateShort(ride.date)} at {ride.time || '(Time not set)'}
                        </h3>
                        <div className="mt-1 flex items-center text-sm text-gray-500">
                          <MapPinIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          <span className="truncate">{ride.pickupLocation || 'No pickup location'} → {ride.destination || 'No destination'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="ml-2 flex-shrink-0 flex">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        ride.status === 'available' ? 'bg-green-100 text-green-800' : 
                        ride.status === 'scheduled' ? 'bg-blue-100 text-blue-800' : 
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {ride.status ? ride.status.charAt(0).toUpperCase() + ride.status.slice(1) : 'Unknown'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex">
                      <div className="flex items-center text-sm text-gray-500">
                        <UserGroupIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                        <p>
                          {ride.availableSeats || 0} of {ride.totalSeats || 0} seats available
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                      <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                      <p>
                        {ride.serviceSession || 'No service session'} Service
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Recent Bookings */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h2 className="text-lg leading-6 font-medium text-gray-900">Recent Bookings</h2>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">Bookings for your rides</p>
            </div>
            <Link 
              to="/driver/bookings" 
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
            >
              View All
            </Link>
          </div>
          
          {loading ? (
            <div className="px-4 py-5 sm:p-6 text-center">
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : recentBookings.length === 0 ? (
            <div className="px-4 py-5 sm:p-6 text-center">
              <p className="text-gray-500">No bookings yet</p>
            </div>
          ) : (
            <div className="border-t border-gray-200 divide-y divide-gray-200">
              {recentBookings.slice(0, 5).map((booking) => (
                <div key={booking.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-indigo-100 rounded-md p-2">
                        <UserGroupIcon className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-sm font-medium text-gray-900">
                          {booking.rider?.firstName || 'Unknown'} {booking.rider?.lastName || 'Rider'}
                        </h3>
                        <div className="mt-1 flex items-center text-sm text-gray-500">
                          <MapPinIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          <span className="truncate">
                            {booking.pickupStreet && booking.pickupCity && booking.pickupPostcode 
                              ? `${booking.pickupStreet}, ${booking.pickupCity} ${booking.pickupPostcode}`
                              : (booking.pickupLocation || 'No pickup location')
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="ml-2 flex-shrink-0 flex">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        booking.status === 'confirmed' ? 'bg-green-100 text-green-800' : 
                        booking.status === 'completed' ? 'bg-blue-100 text-blue-800' : 
                        booking.status === 'cancelled' ? 'bg-red-100 text-red-800' : 
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {booking.status ? booking.status.charAt(0).toUpperCase() + booking.status.slice(1) : 'Pending'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 sm:flex sm:justify-between">
                    <div className="sm:flex">
                      <div className="flex items-center text-sm text-gray-500">
                        <UserGroupIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                        <p>
                          {booking.numberOfSeats || booking.seats || 1} {(booking.numberOfSeats || booking.seats || 1) === 1 ? 'seat' : 'seats'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                      <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                      <p>
                        {booking.Ride?.date ? formatDateShort(booking.Ride.date) : 
                         booking.date ? formatDateShort(booking.date) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div
          className="bg-white overflow-hidden shadow rounded-lg border-2 border-dashed border-gray-300 p-6 hover:border-indigo-500 transition-colors duration-200 cursor-pointer"
          onClick={() => navigate('/driver/rides', { state: { openCreateRide: true } })}
        >
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-md bg-indigo-100 text-indigo-600">
                <CalendarIcon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Create New Ride</h3>
              <p className="mt-2 text-sm text-gray-500">
                Schedule a new ride for church service
              </p>
            </div>
          </div>
        </div>
        
        <Link 
          to="/driver/vehicles" 
          className="bg-white overflow-hidden shadow rounded-lg border-2 border-dashed border-gray-300 p-6 hover:border-indigo-500 transition-colors duration-200"
        >
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-md bg-indigo-100 text-indigo-600">
                <TruckIcon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Add New Vehicle</h3>
              <p className="mt-2 text-sm text-gray-500">
                Register a new vehicle for your rides
              </p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
